import type { CSSProperties } from 'react';

import GglSpinner from './ggl_spinner';
import './ggl_spinner.scss';

/**
 * Оверлей-обёртка спиннера: центрированный блок-заглушка заданной минимальной
 * высоты. Прямая замена legacy-компонента `Spinner` из `spinner_on_top.jsx`.
 *
 * Поведение сохранено 1:1 (та же раскладка и размеры по умолчанию). Исправлены
 * только баги:
 *  - невалидный `display: 'relative'` (браузером игнорировался) — убран;
 *  - опечатка `loadinng` → канонический пропс `loading` (старое имя принимается
 *    для обратной совместимости — его передают скелетон-лоадеры).
 *
 * Реальный полупрозрачный backdrop (план, проблема D5) и миграция 6 потребителей
 * с ручной разметкой `.spinner-ontop-bg` — отдельный MR: там, где родитель будет
 * `position: relative`, оверлей станет абсолютным слоем с фоном. Сейчас оверлей
 * остаётся в потоке, как и раньше, — визуально ничего не меняется.
 */
export interface SpinnerOverlayProps {
  /** Показать спиннер. Канонический пропс (вместо legacy-опечатки `loadinng`). */
  loading?: boolean;
  /**
   * Legacy-опечатка `loadinng` — принимается для обратной совместимости
   * (скелетон-лоадеры `skeleton_*_loader.jsx` передают именно её). При передаче
   * обоих пропсов `loading` приоритетнее.
   */
  loadinng?: boolean;
  /** Отступ снизу блока-заглушки. По умолчанию `'300px'`. */
  marginBottom?: string;
  /** Минимальная высота блока; проксируется в `GglSpinner` как `outerHeight`. По умолчанию `'350px'`. */
  outerHeight?: string;
}

const SpinnerOverlay = ({
  loading,
  loadinng,
  marginBottom = '300px',
  outerHeight = '350px',
}: SpinnerOverlayProps) => {
  // `loading` приоритетнее `loadinng` (если переданы оба). Если ни одного нет —
  // falsy, рендерим null (как и прежний `Spinner` при `loadinng === false`).
  const show = loading ?? loadinng;
  if (!show) return null;

  // Единственный style-атрибут: динамический отступ через CSS-переменную (тот же
  // приём, что --ggl-spinner-min-h в ggl_spinner.tsx). Произвольное px-значение
  // нельзя выразить статической утилитой/классом — вынужденная мера для динамики.
  const blockStyle = { '--ggl-spinner-mb': marginBottom } as CSSProperties;

  return (
    <div className='spinner-ontop-bg'>
      <div className='ggl-spinner-block' style={ blockStyle }>
        <GglSpinner centerBlock outerHeight={ outerHeight } />
      </div>
    </div>
  );
};

SpinnerOverlay.displayName = 'SpinnerOverlay';

/**
 * Legacy-alias: `Spinner` из `spinner_on_top.jsx`.
 *
 * Сохранён как named-export обёртки, чтобы не ломать импорты скелетон-лоадеров
 * (`import { Spinner } from '../spinner/index'`). Поведение идентично
 * {@link SpinnerOverlay}; миграция потребителей на новое имя — отдельный MR.
 */
export const Spinner = SpinnerOverlay;

export default SpinnerOverlay;
